// openEHR RM model — BMM package org.openehr.rm.common.change_control
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { T } from "../_shared.ts";
import type { DV_DATE_TIME } from "../data_types/quantity/date_time.ts";
import type { DV_CODED_TEXT } from "../data_types/text.ts";
import { REVISION_HISTORY, REVISION_HISTORY_ITEM } from "./generic.ts";
import type { ATTESTATION, AUDIT_DETAILS } from "./generic.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Version control abstraction, defining semantics for versioning one complex object.
 */
export class VERSIONED_OBJECT<T> {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Unique identifier of this version container in the form of a UID with no extension. This id will be the same in all instances of the same container in a distributed environment, meaning that it can be understood as the uid of the  virtual version tree.
   */
  uid?: openehr_base.HIER_OBJECT_ID;
  /**
   * Reference to object to which this version container belongs, e.g. the id of the containing EHR or other relevant owning entity.
   */
  owner_id?: openehr_base.OBJECT_REF;
  /**
   * Time of initial creation of this versioned object.
   */
  time_created?: DV_DATE_TIME;
  /**
   * Internal storage for versions.
   * @protected
   */
  protected _versions: VERSION<T>[] = [];

  /**
   * Return the total number of versions in this object.
   * @returns Number of versions
   */
  version_count(): openehr_base.Integer {
    return openehr_base.Integer.from(this._versions.length);
  }

  /**
   * Return a list of ids of all versions in this object.
   * @returns Array of version IDs
   */
  all_version_ids(): openehr_base.OBJECT_VERSION_ID[] {
    return this._versions
      .map((v) => {
        if (v instanceof ORIGINAL_VERSION) {
          return v.uid;
        } else if (v instanceof IMPORTED_VERSION) {
          return v.item?.uid;
        }
        return undefined;
      })
      .filter((id): id is openehr_base.OBJECT_VERSION_ID => id !== undefined);
  }

  /**
   * Return a list of all versions in this object.
   * @returns Array of all versions
   */
  all_versions(): VERSION<T>[] {
    return [...this._versions];
  }

  /**
   * True if a version for time  \`_a_time_\` exists.
   * @param a_time - The time to search for
   * @returns Boolean indicating if version exists at that time
   */
  has_version_at_time(a_time: DV_DATE_TIME): openehr_base.Boolean {
    const targetTime = a_time.value || "";
    for (const version of this._versions) {
      if (version.commit_audit?.time_committed?.value === targetTime) {
        return openehr_base.Boolean.from(true);
      }
    }
    return openehr_base.Boolean.from(false);
  }

  /**
   * True if a version with \`_a_version_uid_\` exists.
   * @param a_version_uid - The version ID to search for
   * @returns Boolean indicating if version with that ID exists
   */
  has_version_id(
    a_version_uid: openehr_base.OBJECT_VERSION_ID,
  ): openehr_base.Boolean {
    const targetId = a_version_uid.value || "";
    for (const version of this._versions) {
      let versionId: string | undefined;
      if (version instanceof ORIGINAL_VERSION) {
        versionId = version.uid?.value;
      } else if (version instanceof IMPORTED_VERSION) {
        versionId = version.item?.uid?.value;
      }
      if (versionId === targetId) {
        return openehr_base.Boolean.from(true);
      }
    }
    return openehr_base.Boolean.from(false);
  }

  /**
   * Return the version with \`_uid_\` =  \`_a_version_uid_\`.
   *
   * @param a_version_uid - The version ID to find
   * @returns The matching version
   */
  version_with_id(a_version_uid: openehr_base.OBJECT_VERSION_ID): VERSION<T> | undefined {
    const targetId = a_version_uid.value || "";
    for (const version of this._versions) {
      let versionId: string | undefined;
      if (version instanceof ORIGINAL_VERSION) {
        versionId = version.uid?.value;
      } else if (version instanceof IMPORTED_VERSION) {
        versionId = version.item?.uid?.value;
      }
      if (versionId === targetId) {
        return version;
      }
    }
    return undefined;
  }

  /**
   * True if version with \`_a_version_uid_\` is an \`ORIGINAL_VERSION\`.
   * @param a_version_uid - The version ID to check
   * @returns Boolean indicating if the version is an ORIGINAL_VERSION
   */
  is_original_version(
    a_version_uid: openehr_base.OBJECT_VERSION_ID,
  ): openehr_base.Boolean {
    const version = this.version_with_id(a_version_uid);
    return openehr_base.Boolean.from(version instanceof ORIGINAL_VERSION);
  }

  /**
   * Return the version for time  \`_a_time_\`.
   * @param a_time - The time to search for
   * @returns The version at that time
   */
  version_at_time(a_time: DV_DATE_TIME): VERSION<T> | undefined {
    const targetTime = a_time.value || "";
    for (const version of this._versions) {
      if (version.commit_audit?.time_committed?.value === targetTime) {
        return version;
      }
    }
    return undefined;
  }

  /**
   * History of all audits and attestations in this versioned repository.
   * @returns Revision history
   */
  revision_history(): REVISION_HISTORY {
    const history = new REVISION_HISTORY();
    history.items = this._versions.map((version) => {
      const item = new REVISION_HISTORY_ITEM();
      if (version instanceof ORIGINAL_VERSION) {
        item.version_id = version.uid;
        // Populate audits from commit_audit
        if (version.commit_audit) {
          item.audits = [version.commit_audit];
        }
      } else if (version instanceof IMPORTED_VERSION) {
        item.version_id = version.item?.uid;
        if (version.commit_audit) {
          item.audits = [version.commit_audit];
        }
      }
      return item;
    });
    return history;
  }

  /**
   * Return the most recently added version (i.e. on trunk or any branch).
   * @returns The latest version
   */
  latest_version(): VERSION<T> | undefined {
    if (this._versions.length === 0) return undefined;
    // Return the last added version
    return this._versions[this._versions.length - 1];
  }

  /**
   * Return the most recently added trunk version.
   * @returns The latest trunk (non-branch) version
   */
  latest_trunk_version(): VERSION<T> | undefined {
    // Trunk versions don't have branch numbers in their version_tree_id
    for (let i = this._versions.length - 1; i >= 0; i--) {
      const version = this._versions[i];
      if (version instanceof ORIGINAL_VERSION && version.uid) {
        // Check if it's a trunk version (not a branch)
        const versionTreeId = version.uid.version_tree_id?.();
        if (versionTreeId && !versionTreeId.is_branch?.().value) {
          return version;
        }
      }
    }
    // Return the latest version if no trunk-specific version found
    return this.latest_version();
  }

  /**
   * Return the lifecycle state from the latest trunk version. Useful for determining if the version container is logically deleted.
   * @returns The lifecycle state
   */
  trunk_lifecycle_state(): DV_CODED_TEXT | undefined {
    const trunkVersion = this.latest_trunk_version();
    if (trunkVersion instanceof ORIGINAL_VERSION) {
      return trunkVersion.lifecycle_state;
    }
    return undefined;
  }

  /**
   * Add a new original version.
   * @param a_contribution - Reference to the contribution
   * @param a_new_version_uid - UID for the new version
   * @param a_preceding_version_id - UID of the preceding version
   * @param an_audit - Audit details
   * @param a_lifecycle_state - Lifecycle state
   * @param a_data - The data content
   * @param signing_key - Key for signing (not used in this implementation)
   */
  commit_original_version(
    a_contribution: openehr_base.OBJECT_REF,
    a_new_version_uid: openehr_base.OBJECT_VERSION_ID,
    a_preceding_version_id: openehr_base.OBJECT_VERSION_ID | undefined,
    an_audit: AUDIT_DETAILS,
    a_lifecycle_state: DV_CODED_TEXT,
    a_data: T,
    _signing_key: openehr_base.String,
  ): void {
    const version = new ORIGINAL_VERSION<T>();
    version.uid = a_new_version_uid;
    version.preceding_version_uid = a_preceding_version_id;
    version.lifecycle_state = a_lifecycle_state;
    version.data = a_data;
    version.contribution = a_contribution;
    version.commit_audit = an_audit;
    
    this._versions.push(version);
  }

  /**
   * Add a new original merged version. This commit function adds a parameter containing the ids of other versions merged into the current one.
   * @param a_contribution - Reference to the contribution
   * @param a_new_version_uid - UID for the new version
   * @param a_preceding_version_id - UID of the preceding version
   * @param an_audit - Audit details
   * @param a_lifecycle_state - Lifecycle state
   * @param a_data - The data content
   * @param an_other_input_uids - UIDs of other merged versions
   * @param signing_key - Key for signing (not used in this implementation)
   */
  commit_original_merged_version(
    a_contribution: openehr_base.OBJECT_REF,
    a_new_version_uid: openehr_base.OBJECT_VERSION_ID,
    a_preceding_version_id: openehr_base.OBJECT_VERSION_ID | undefined,
    an_audit: AUDIT_DETAILS,
    a_lifecycle_state: DV_CODED_TEXT,
    a_data: T,
    an_other_input_uids: openehr_base.OBJECT_VERSION_ID[] | undefined,
    _signing_key: openehr_base.String,
  ): void {
    const version = new ORIGINAL_VERSION<T>();
    version.uid = a_new_version_uid;
    version.preceding_version_uid = a_preceding_version_id;
    version.lifecycle_state = a_lifecycle_state;
    version.data = a_data;
    version.contribution = a_contribution;
    version.commit_audit = an_audit;
    version.other_input_version_uids = an_other_input_uids;
    
    this._versions.push(version);
  }

  /**
   * Add a new imported version. Details of version id etc come from the \`ORIGINAL_VERSION\` being committed.
   * @param a_contribution - Reference to the contribution
   * @param an_audit - Audit details
   * @param a_version - The original version being imported
   */
  commit_imported_version(
    a_contribution: openehr_base.OBJECT_REF,
    an_audit: AUDIT_DETAILS,
    a_version: ORIGINAL_VERSION<T>,
  ): void {
    const imported = new IMPORTED_VERSION<T>();
    imported.item = a_version;
    imported.contribution = a_contribution;
    imported.commit_audit = an_audit;
    
    this._versions.push(imported);
  }

  /**
   * Add a new attestation to a specified original version. Attestations can only be added to Original versions.
   * @param an_attestation - The attestation to add
   * @param a_ver_id - The version ID to add attestation to
   * @param signing_key - Key for signing (not used in this implementation)
   */
  commit_attestation(
    an_attestation: ATTESTATION,
    a_ver_id: openehr_base.OBJECT_VERSION_ID,
    _signing_key: openehr_base.String,
  ): void {
    const version = this.version_with_id(a_ver_id);
    if (version instanceof ORIGINAL_VERSION) {
      if (!version.attestations) {
        version.attestations = [];
      }
      version.attestations.push(an_attestation);
    } else {
      throw new Error("Attestations can only be added to ORIGINAL_VERSION instances");
    }
  }
}

/**
 * Documents a Contribution (change set) of one or more versions added to a change-controlled repository.
 */
export class CONTRIBUTION {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Unique identifier for this Contribution.
   */
  uid?: openehr_base.HIER_OBJECT_ID;
  /**
   * Set of references to Versions causing changes to this EHR. Each contribution contains a list of versions, which may include paths pointing to any number of versionable items, i.e. items of types such as \`COMPOSITION\` and \`FOLDER\`.
   */
  versions?: undefined;
  /**
   * Audit trail corresponding to the committal of this Contribution.
   */
  audit?: AUDIT_DETAILS;
}

/**
 * Abstract model of one Version within a Version container, containing data, commit audit trail, and the identifier of its Contribution.
 */
export abstract class VERSION<T> {
  /**
   * Contribution in which this version was added.
   */
  contribution?: openehr_base.OBJECT_REF;
  /**
   * Internal storage for signature
   * @protected
   */
  protected _signature?: openehr_base.String;

  /**
   * OpenPGP digital signature or digest of content committed in this Version.
   */
  get signature(): string | undefined {
    return this._signature?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for signature.
   * Use this to access openehr_base.String methods.
   */
  get $signature(): openehr_base.String | undefined {
    return this._signature;
  }

  /**
   * Sets signature from either a primitive value or openehr_base.String wrapper.
   */
  set signature(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._signature = undefined;
    } else if (typeof val === "string") {
      this._signature = openehr_base.String.from(val);
    } else {
      this._signature = val;
    }
  }

  /**
   * Audit trail corresponding to the committal of this version to the \`VERSIONED_OBJECT\`.
   */
  commit_audit?: AUDIT_DETAILS;
  /**
   * Unique identifier of this \`VERSION\`, in the form of an \`{object_id, a version_tree_id, creating_system_id}\` triple, where the \`_object_id_\` has the same value as the containing \`VERSIONED_OBJECT _uid_\`.
   */
  uid?: openehr_base.OBJECT_VERSION_ID;

  /**
   * Unique identifier of the version of which this version is a modification; Void if this is the first version.
   */
  preceding_version_uid?: openehr_base.OBJECT_VERSION_ID;

  /**
   * The data of this Version.
   */
  data?: T;

  /**
   * Lifecycle state of this version; coded by openEHR vocabulary \`version lifecycle state\`.
   */
  lifecycle_state?: DV_CODED_TEXT;

  /**
   * A canonical serial form of this Version, suitable for generating reliable hashes and signatures.
   * Returns a JSON string representation.
   * @returns Canonical string form
   */
  canonical_form(): openehr_base.String {
    // Create a canonical JSON representation
    const canonicalObj = {
      uid: this.uid?.value,
      preceding_version_uid: this.preceding_version_uid?.value,
      lifecycle_state: this.lifecycle_state?.defining_code?.code_string,
      contribution: this.contribution?.id?.value,
      commit_audit: {
        time_committed: this.commit_audit?.time_committed?.value,
        committer: this.commit_audit?.committer,
        change_type: this.commit_audit?.change_type?.defining_code?.code_string,
        description: this.commit_audit?.description?.value,
      },
    };
    return openehr_base.String.from(JSON.stringify(canonicalObj));
  }

  /**
   * Copy of the owning \`VERSIONED_OBJECT._uid_\` value; extracted from the local \`_uid_\` property's \`_object_id_\`.
   * @returns HIER_OBJECT_ID representing the owner
   */
  owner_id(): openehr_base.HIER_OBJECT_ID {
    const versionUid = this.uid;
    if (!versionUid) {
      throw new Error("VERSION uid is not set");
    }
    // Extract the object_id part from the version uid
    // VERSION_UID format: object_id::creating_system_id::version_tree_id
    const objectId = versionUid.object_id?.();
    if (!objectId) {
      throw new Error("VERSION uid.object_id is not set");
    }
    const ownerId = new openehr_base.HIER_OBJECT_ID();
    ownerId.value = objectId.value;
    return ownerId;
  }

  /**
   * True if this Version represents a branch. Derived from \`_uid_\` attribute.
   * @returns Boolean indicating if this is a branch version
   */
  is_branch(): openehr_base.Boolean {
    const versionUid = this.uid;
    if (!versionUid) {
      return openehr_base.Boolean.from(false);
    }
    // Check the version_tree_id to determine if it's a branch
    const versionTreeId = versionUid.version_tree_id?.();
    if (versionTreeId && typeof versionTreeId.is_branch === "function") {
      return versionTreeId.is_branch();
    }
    return openehr_base.Boolean.from(false);
  }
}

/**
 * Versions whose content is an \`ORIGINAL_VERSION\` copied from another location; this class inherits \`_commit_audit_\` and \`_contribution_\` from \`VERSION<T>\`, providing imported versions with their own audit trail and Contribution, distinct from those of the imported \`ORIGINAL_VERSION\`.
 */
export class IMPORTED_VERSION<T> extends VERSION<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The \`ORIGINAL_VERSION\` object that was imported.
   */
  item?: ORIGINAL_VERSION<T>;
}

/**
 * A Version containing locally created content and optional attestations.
 */
export class ORIGINAL_VERSION<T> extends VERSION<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Identifiers of other versions whose content was merged into this version, if any.
   */
  other_input_version_uids?: openehr_base.OBJECT_VERSION_ID[];
  /**
   * Set of attestations relating to this version.
   */
  attestations?: ATTESTATION[];
  
  /**
   * True if this Version was created from more than just the preceding (checked out) version.
   * @returns Boolean indicating if this version is merged
   */
  is_merged(): openehr_base.Boolean {
    // A version is merged if it has other_input_version_uids
    return openehr_base.Boolean.from(
      this.other_input_version_uids !== undefined && 
      this.other_input_version_uids.length > 0
    );
  }
}
